import { datadogRum } from "@datadog/browser-rum";
import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { FC, ReactNode, SVGProps, useEffect } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { getAuthClaims, getPlatform, getUser } from "api/methods";
import { Icon } from "atoms/icon/Icon";
import Warning from "atoms/icon/icons/ic_warning_ol.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import useDunningMembership from "hooks/useDunningMemberships";
import { AdminImpersonationBanner } from "molecules/admin-impersonation-banner/AdminImpersonationBanner";
import { TopNavActions } from "molecules/navbars/TopNavActions";
import Services from "services/Services";
import { clearAcknowledgedPopupPromotionIds } from "utils/functions";

type BaseProps = {
  children?: ReactNode;
  footer?: ReactNode;
  startIconSVG?: undefined;
  onStartIconClick?: undefined;
  hideShadow?: boolean;
  /** Extra classes for the nav's root (e.g. grid placement on desktop). */
  className?: string;
  contentClassName?: string;
  showHeaderItems?: boolean;
  /** Used for HomePage.tsx, because of the way we set TopNav on every page.
   * To remove after setting TopNav at a higher level(AppLayout, where Footer also is).
   */
  withAffirmOnTop?: boolean;
  backgroundVariant?: "transparent" | "default";
};

type WithIconProps = {
  startIconSVG: FC<SVGProps<SVGSVGElement>>;
  onStartIconClick: () => void;
} & Omit<BaseProps, "startIconSVG" | "onStartIconClick">;

type Props = BaseProps | WithIconProps;

const TopNav = ({
  children,
  footer,
  startIconSVG,
  onStartIconClick,
  hideShadow = false,
  showHeaderItems = true,
  className,
  contentClassName,
  withAffirmOnTop,
  backgroundVariant = "default",
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const manageMembershipsPagePath = "/account/membership/manage";
  const manageMembershipsPageMatch = useMatch(manageMembershipsPagePath);
  const { dunningMembership } = useDunningMembership();
  const { data: authClaims } = useQuery({ ...getAuthClaims.keyed() });

  useQuery({ ...getPlatform.keyed() });

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  useEffect(() => {
    (async () => {
      if (user?.isBlocked) {
        clearAcknowledgedPopupPromotionIds();
        await Services.auth.clearAll();
        datadogRum.clearUser();
        navigate("/signin");
      }
    })();
  }, [user?.isBlocked, navigate]);

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  return (
    <div
      className={cx(
        "sticky top-0 z-20 desk:static",
        backgroundVariant === "default" && "bg-white",
        backgroundVariant === "transparent" && "bg-transparent",
        withAffirmOnTop
          ? "top-[env(safe-area-inset-top)] desk:top-auto"
          : "pt-[env(safe-area-inset-top)] desk:pt-0",
        !hideShadow && "shadow-[0px_2px_4px_rgba(0,0,0,0.05)] desk:shadow-none",
        className,
      )}
    >
      {isAdminImpersonation && !!user && (
        <AdminImpersonationBanner
          platformUserDisplayName={
            user.email || (user.firstName && user.lastName)
              ? user.email || `${user.firstName} ${user.lastName}`
              : user.phoneNumber
          }
        />
      )}
      {dunningMembership.status && (
        <div className="sticky top-0 z-10 flex items-center justify-between bg-[#8B0000] py-1.5 pl-4 pr-3 text-bold2 text-white">
          <div className="flex gap-2">
            <Icon svg={Warning} size="medium" color="white" />
            {t("topNav.membershipPaymentFailedRecent")}
          </div>

          {!manageMembershipsPageMatch && (
            <Link
              to={manageMembershipsPagePath}
              className="flex items-center gap-1 rounded-full border-2 border-white bg-white px-4 py-1 text-bold2 text-[#8B0000]"
              style={{ animation: "urgentPulse 1.5s ease-in-out infinite" }}
            >
              {t("topNav.fix")}
            </Link>
          )}
        </div>
      )}

      <div
        className={cn(
          "flex h-[54px] items-center justify-between pb-[5px] pt-[5px] font-bold desk:h-auto desk:py-4",
          "desk:mx-auto desk:w-full desk:max-w-screen-xl desk2xl:max-w-screen-2xl",
          startIconSVG ? "pr-5 desk:pr-6 desklg:pr-8" : "px-5 desk:px-6 desklg:px-8",
          contentClassName,
        )}
      >
        {startIconSVG && (
          <IconButton svg={startIconSVG} size="small" style="iconOnly" onClick={onStartIconClick} />
        )}
        <div className="flex flex-1 items-center pl-1 [&:not(:first-child)]:justify-start">
          {children}
        </div>
        {startIconSVG && <div className="invisible w-6"></div>}
        {showHeaderItems && (
          <div className="flex items-center desk:hidden">
            <TopNavActions />
          </div>
        )}
      </div>
      <div>{footer}</div>
    </div>
  );
};

export default TopNav;
